// src/components/AdjustmentComposer.tsx
//
// ★★ WAVE 1c — Annex A stories 3 and 4 from a keyboard, WITHOUT a second path
// to the money.
//
// The form below does not adjust a bill. It asks the server to compose a
// one-record `PRS2-HSP-BILL` delivery, deposit it on the counterparty's channel
// and read it back through the identical intake a delivered file goes through.
// Everything that decides money — the drift comparison against the original's
// stored rule and rate, the service-date inheritance check, the grace period,
// `ADJUSTMENT_ORIGINAL_CAPPED`, S-034's cap re-application — stays in
// `hsp-adjustment.ts`, which this component knows nothing about.
//
// ⚠ **What that costs the officer, and why it is worth it:** a refusal arrives
// as a REJECTED record inside a PROCESSED delivery rather than as a validation
// message on the field that caused it. That is a genuinely worse form
// experience, and it is the correct trade — the alternative is a second
// implementation of the money rules that would disagree with the first one
// silently. The panel below therefore renders the grammar's own remedy text,
// which is written for a counterparty to act on.
import { useState } from 'react'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { REFUSAL_REMEDY, type RefusalKind } from '@prs2/contracts'
import { targetValue } from '../lib/sgds-dom'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { ErrorCard } from './StateCards'
import { ConfirmWrite } from './ConfirmWrite'
import type { InboundAdjustmentResponse } from '../lib/types'
import { NETWORK_FAILURE } from '../lib/messages'

/** Cents, from a decimal amount an officer types. Never a float in the payload. */
function parseAmountToCents(text: string): number | undefined {
  const trimmed = text.trim()
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) return undefined
  // ⚠ Assembled from the digits rather than `Math.round(Number(x) * 100)`:
  // 8.07 * 100 is 806.9999999999999 in binary floating point, and a correction
  // that lands a cent out is exactly the class of defect this product exists to
  // prevent.
  const negative = trimmed.startsWith('-')
  const [whole, fraction = ''] = trimmed.replace('-', '').split('.')
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return negative ? -cents : cents
}

/**
 * Dollars and cents from an integer cent count, assembled from the digits for
 * the same reason `parseAmountToCents` is — the confirmation sentence must show
 * exactly the amount that will reach the wire, never a float's rendering of it.
 */
function formatCentsExact(cents: number): string {
  const magnitude = Math.abs(cents)
  return `$${Math.floor(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`
}

export function AdjustmentComposer({
  billReference,
  onApplied,
}: {
  billReference: string
  onApplied: () => void
}) {
  // ⚠ The channel list is the SAME one the upload screen reads, narrowed to
  // this caller's own grants by the server. If the officer may not deposit
  // anywhere, they may not compose a correction either, and the reason is the
  // same one — so it is read from one place rather than decided twice.
  const channels = useApi(() => api.inboundChannels(), [])
  const [sourceReference, setSourceReference] = useState('')
  const [amount, setAmount] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<InboundAdjustmentResponse | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  const usable = channels.data?.channels ?? []
  const [channelId, setChannelId] = useState('')
  const chosen = channelId !== '' ? channelId : (usable[0]?.channelId ?? '')
  const deltaCents = parseAmountToCents(amount)
  const amountProblem =
    amount.trim().length === 0
      ? undefined
      : deltaCents === undefined
        ? 'Enter an amount in dollars and cents, e.g. 25.00 for an increase or -25.00 for a decrease.'
        : deltaCents === 0
          ? 'A correction of zero moves nothing and has no effect to record.'
          : undefined

  /**
   * ★★ WAVE 26 — Submit FREEZES a sentence and asks; the write happens only from
   * the confirmation (`ConfirmWrite`'s header says why every irreversible write
   * does this now). A correction is an append-only delivery: once read, it is a
   * record in the bill's chain and can only be answered by another one. The
   * sentence names the bill, the direction and the amount, and the channel, from
   * the fields as they were when Submit was pressed.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  function askToSubmit(): void {
    if (chosen === '' || deltaCents === undefined || deltaCents === 0 || sourceReference.trim().length === 0) return
    const channel = usable.find((c) => c.channelId === chosen)
    const attribution = channel === undefined ? `channel ${chosen}` : `channel ${chosen} (${channel.counterpartyCode})`
    setConfirming(
      `Submit ${deltaCents > 0 ? 'an upward' : 'a downward'} correction of ${formatCentsExact(deltaCents)} against bill ` +
        `${billReference}, under your reference ${sourceReference.trim()}, as a delivery on ${attribution}. It is an ` +
        'append-only correction validated like a provider delivery: the original’s stored rule and rate, its service ' +
        'date and the grace period all apply. There is no undo. A mistake is corrected by another record.',
    )
  }

  async function performWrite(): Promise<void> {
    setConfirming(null)
    if (chosen === '' || deltaCents === undefined || deltaCents === 0 || sourceReference.trim().length === 0) return
    setSending(true)
    setFailure(null)
    setResult(null)
    try {
      const outcome = await api.composeInboundAdjustment(chosen, {
        sourceReference: sourceReference.trim(),
        originalReference: billReference,
        deltaCents,
      })
      setResult(outcome)
      // The bill and its chain have changed if the record was accepted; re-read
      // rather than leaving the officer looking at figures that predate them.
      if (outcome.accepted > 0) onApplied()
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError ? error.message : `${NETWORK_FAILURE} Nothing was submitted.`,
      )
    } finally {
      setSending(false)
    }
  }

  // ⚠ Nothing is rendered at all when this deployment wires no channel the
  // caller may use — not a disabled button. There is no action to offer and no
  // wording that would make one meaningful; the Upload screen is where the
  // absence is explained, in one place.
  if (channels.error !== null || channels.initialLoading || usable.length === 0) return null

  if (!open) {
    return (
      <div className="mt-4 pt-no-print">
        <SgdsButton variant="outline" type="button" onClick={() => setOpen(true)}>
          Submit a correction
        </SgdsButton>
      </div>
    )
  }

  return (
    <div className="mt-4 flex flex-col gap-3 pt-no-print">
      <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
        A correction is submitted as a <strong>delivery on the counterparty&rsquo;s channel</strong>, exactly as one
        arriving in a file would be, and is validated by the same rules: the original&rsquo;s stored rule and rate,
        its date of medical service, and the grace period. It is not applied directly.
      </p>

      {usable.length > 1 && (
        <div className="max-w-[28rem]">
          {/* ★ WAVE 26 — the house `SgdsSelect`, not a native `<select>`. */}
          <SgdsSelect
            label="Channel"
            value={chosen}
            onSgdsChange={(e) => {
              setChannelId(targetValue(e))
              // A frozen sentence names a channel; a new choice makes it stale.
              setConfirming(null)
            }}
          >
            {usable.map((c) => (
              <SgdsSelectOption key={c.channelId} value={c.channelId}>
                {c.channelId}, {c.counterpartyCode}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>
        </div>
      )}

      <label className="flex flex-col gap-1 max-w-[28rem]">
        <span className="font-semibold">Your reference for this correction</span>
        <input
          className="pt-input"
          value={sourceReference}
          onChange={(e) => {
            setSourceReference(e.target.value)
            setConfirming(null)
          }}
          aria-describedby="adj-ref-hint"
        />
        <span id="adj-ref-hint" className="text-sm text-[var(--sgds-body-color-subtle)]">
          Your own reference, unique among your deliveries, not PRS&rsquo;s bill reference.
        </span>
      </label>

      <label className="flex flex-col gap-1 max-w-[28rem]">
        <span className="font-semibold">Amount</span>
        <input
          className="pt-input"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value)
            setConfirming(null)
          }}
          aria-invalid={amountProblem !== undefined}
          aria-describedby="adj-amount-hint"
        />
        <span id="adj-amount-hint" className="text-sm text-[var(--sgds-body-color-subtle)]">
          Dollars and cents. <strong>Positive increases</strong> the bill, <strong>negative decreases</strong> it:
          the sign decides whether this is filed as an upward or a downward correction.
        </span>
        {amountProblem !== undefined && (
          <span role="alert" className="text-sm font-semibold text-[var(--sgds-danger-color-emphasis)]">
            {amountProblem}
          </span>
        )}
      </label>

      <div className="flex gap-2">
        <SgdsButton
          type="button"
          disabled={
            sending ||
            confirming !== null ||
            sourceReference.trim().length === 0 ||
            deltaCents === undefined ||
            deltaCents === 0
          }
          onClick={askToSubmit}
        >
          {sending ? 'Submitting…' : 'Submit correction'}
        </SgdsButton>
        <SgdsButton
          variant="outline"
          type="button"
          disabled={sending}
          onClick={() => {
            setOpen(false)
            setConfirming(null)
          }}
        >
          Cancel
        </SgdsButton>
      </div>

      {confirming !== null && (
        <ConfirmWrite
          summary={confirming}
          confirmLabel="Yes, submit the correction"
          variant="danger"
          onConfirm={() => void performWrite()}
          onCancel={() => setConfirming(null)}
        />
      )}

      {failure !== null && (
        <div role="alert">
          <ErrorCard title="The correction was refused" message={failure} />
        </div>
      )}

      {result !== null && (
        <div role="status" aria-live="polite">
          {result.accepted > 0 ? (
            <p className="font-semibold text-[var(--sgds-success-color-emphasis)]">
              Correction applied as {result.indicator === 'UA' ? 'an upward' : 'a downward'} adjustment. The
              running totals above have been re-read.
            </p>
          ) : (
            // ⚠ The delivery was READ and the record was REFUSED — two different
            // sentences. Saying "that did not work" would hide that a durable,
            // readable outcome exists for it in the records enquiry.
            <>
              <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">
                The delivery was read and the correction was refused. Nothing was changed.
              </p>
              {result.reasonKind !== null && (
                <p className="mt-1 text-sm">
                  {REFUSAL_REMEDY[result.reasonKind as RefusalKind] ?? result.reasonKind}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
